// apps/client/src/pages/leads/edm/EdmCampaignsPage.tsx
// /leads/edm: the company's eDM campaigns. Reads only through the
// edmCampaignAction callable (listCampaigns); no Firestore SDK reads.
//
// Plan-v3 core E2, each behind its own flag (absent = the Phase 1c page):
// - edm_preferences: a "Topics" button for company-wide callers.
// - edm_tracking: tick up to 10 sent campaigns and compare them side by side
//   (one getStats call with campaignIds; opens and clicks are approximate).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { BarChart3, FileUp, Globe, ListChecks, MailPlus, Mails, Route as RouteIcon, Users, X } from 'lucide-react';
import { Spinner } from '../../../components/common/Spinner';
import { PageHeader } from '../../../components/common/PageHeader';
import { SkeletonTablePage } from '../../../components/common/SkeletonLoader';
import { Pagination } from '../../../components/common/Pagination';
import { useAuth } from '../../../hooks/useAuth';
import { EDM_MAX_COMPARE, useEdmCampaigns } from '../../../hooks/useEdmCampaigns';
import { isEdmJourneysEnabled } from '../../../hooks/useEdmJourneys';
import type { EdmCampaignProjection, EdmCampaignStats } from '../../../hooks/useEdmCampaigns';
import {
  EDM_UI,
  canImportEdmContacts,
  edmErrorMessage,
  formatBrisbaneDateTime,
  isEdmPreferencesEnabled,
  isEdmTrackingEnabled,
  statusChipClass,
  statusLabel,
} from '../../../utils/edm';

const DEFAULT_PAGE_SIZE = 25;

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

const count = (n: number | null | undefined): string =>
  typeof n === 'number' ? n.toLocaleString('en-AU') : '–';

/** Share of `sent`, one decimal place; '–' when unknown or nothing was sent. */
export function ratePct(part: number | null | undefined, sent: number | null | undefined): string {
  if (typeof part !== 'number' || typeof sent !== 'number' || sent <= 0) return '–';
  return `${((part / sent) * 100).toFixed(1)}%`;
}

interface ComparePanelProps {
  rows: EdmCampaignStats[];
  /** Ticked campaigns the server returned no stats for. */
  missing: string[];
  nameOf: (id: string) => string;
  onClose: () => void;
}

const ComparePanel: React.FC<ComparePanelProps> = ({ rows, missing, nameOf, onClose }) => (
  <section className="rounded-xl border border-gray-700 bg-gray-800/40" aria-label="Campaign comparison">
    <div className="flex items-center justify-between gap-3 border-b border-gray-700 px-4 py-3">
      <h2 className="flex items-center gap-2 text-base font-semibold text-gray-100">
        <BarChart3 className="h-4 w-4 text-blue-400" /> Compare campaigns
      </h2>
      <button type="button" className="text-gray-400 hover:text-gray-100" aria-label="Close comparison" onClick={onClose}>
        <X className="h-4 w-4" />
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
            <th className="px-4 py-3">Campaign</th>
            <th className="px-4 py-3">Sent</th>
            <th className="px-4 py-3">Delivered</th>
            <th className="px-4 py-3">Bounced</th>
            <th className="px-4 py-3">Unsubscribed</th>
            <th className="px-4 py-3">Opens (approx.)</th>
            <th className="px-4 py-3">Open rate</th>
            <th className="px-4 py-3">Clicks (approx.)</th>
            <th className="px-4 py-3">Click rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const e = r.engagement ?? null;
            return (
              <tr key={r.campaignId} className="border-b border-gray-800">
                <td className="px-4 py-3 text-gray-100">{nameOf(r.campaignId)}</td>
                <td className="px-4 py-3 text-gray-300">{count(r.sent)}</td>
                <td className="px-4 py-3 text-gray-300">{count(r.delivered)}</td>
                <td className="px-4 py-3 text-gray-300">{count(r.bounced)}</td>
                <td className="px-4 py-3 text-gray-300">{count(r.unsubscribed)}</td>
                <td className="px-4 py-3 text-gray-300">{e ? count(e.uniqueOpens) : 'Not tracked'}</td>
                <td className="px-4 py-3 text-gray-300">{e ? ratePct(e.uniqueOpens, r.sent) : '–'}</td>
                <td className="px-4 py-3 text-gray-300">{e ? count(e.uniqueClicks) : 'Not tracked'}</td>
                <td className="px-4 py-3 text-gray-300">{e ? ratePct(e.uniqueClicks, r.sent) : '–'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {missing.length > 0 && (
      <p className="px-4 pt-3 text-xs text-amber-300">
        No numbers yet for: {missing.map(nameOf).join(', ')}.
      </p>
    )}
    <p className="px-4 py-3 text-xs text-gray-400">
      Opens and clicks are approximate: some email apps load images automatically or block them, and some
      security scanners follow links. Rates are a share of emails sent.
    </p>
  </section>
);

const EdmCampaignsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const { claims } = useAuth();
  const navigate = useNavigate();
  const companyId = ctx?.userData?.companyId;
  const role = ctx?.userData?.role ?? claims?.role;
  const api = useEdmCampaigns(companyId);
  const flags = ctx?.companyData?.featureFlags;
  const trackingEnabled = isEdmTrackingEnabled(flags);
  const preferencesEnabled = isEdmPreferencesEnabled(flags);
  // Plan JOURNEYS J8 (orchestrator-approved): the Journeys entry, company-wide callers only.
  const journeysEnabled = isEdmJourneysEnabled(flags);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [campaigns, setCampaigns] = useState<EdmCampaignProjection[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listCampaigns(page, pageSize)
      .then((res) => {
        // The server orders listCampaigns by updatedAt desc; a client sort
        // would reorder within one page only.
        setCampaigns(res.campaigns);
        setTotal(res.total);
      })
      .catch((err) => setError(edmErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [api, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Compare (edm_tracking): ticked ids survive paging; names are remembered
  // from the page they were ticked on.
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareNames, setCompareNames] = useState<Record<string, string>>({});
  // The comparison shown, and the ids it was run for: any change to the
  // ticked set hides it until Compare is pressed again (never a stale table).
  const [compareResult, setCompareResult] = useState<{ ids: string[]; rows: EdmCampaignStats[] } | null>(null);
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const compareSet = useMemo(() => new Set(compareIds), [compareIds]);

  const toggleCompare = (c: EdmCampaignProjection) => {
    setCompareNames((m) => ({ ...m, [c.id]: c.name }));
    setCompareResult(null);
    setCompareError(null);
    setCompareIds((ids) => {
      if (ids.includes(c.id)) return ids.filter((x) => x !== c.id);
      if (ids.length >= EDM_MAX_COMPARE) return ids;
      return [...ids, c.id];
    });
  };

  const runCompare = () => {
    if (compareIds.length < 2) return;
    const ids = compareIds;
    setCompareBusy(true);
    setCompareError(null);
    // One getStats call with campaignIds; any inaccessible id refuses all.
    api
      .getStatsMany(ids)
      .then((res) => {
        // Show rows in the order the campaigns were ticked.
        const byId = new Map(res.stats.map((r) => [r.campaignId, r]));
        setCompareResult({
          ids,
          rows: ids.map((id) => byId.get(id)).filter((r): r is EdmCampaignStats => r !== undefined),
        });
      })
      .catch((err) => {
        setCompareResult(null);
        setCompareError(edmErrorMessage(err));
      })
      .finally(() => setCompareBusy(false));
  };

  const canImport = canImportEdmContacts(role, claims?.canSeeAllOffices);
  const officeScopedAdmin = role === 'administrator' && claims?.canSeeAllOffices !== true;

  const newButton = (
    <button type="button" className={EDM_UI.primaryButton} onClick={() => navigate('/leads/edm/new')}>
      <MailPlus className="h-4 w-4" /> New campaign
    </button>
  );

  if (loading && campaigns === null) return <SkeletonTablePage tableRows={6} tableColumns={6} />;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Mails}
        title="eDM Campaigns"
        subtitle="Email one message to a frozen list of your leads, clients and imported contacts"
        onRefresh={load}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            {canImport && (
              <>
                {journeysEnabled && (
                  <button type="button" className={EDM_UI.secondaryButton} onClick={() => navigate('/leads/edm/journeys')}>
                    <RouteIcon className="h-4 w-4" /> Journeys
                  </button>
                )}
                {preferencesEnabled && (
                  <button type="button" className={EDM_UI.secondaryButton} onClick={() => navigate('/leads/edm/topics')}>
                    <ListChecks className="h-4 w-4" /> Topics
                  </button>
                )}
                <button type="button" className={EDM_UI.secondaryButton} onClick={() => navigate('/leads/edm/contacts')}>
                  <Users className="h-4 w-4" /> Contacts
                </button>
                <button type="button" className={EDM_UI.secondaryButton} onClick={() => navigate('/leads/edm/domain')}>
                  <Globe className="h-4 w-4" /> Sending domain
                </button>
                <button type="button" className={EDM_UI.secondaryButton} onClick={() => navigate('/leads/edm/import')}>
                  <FileUp className="h-4 w-4" /> Import contacts
                </button>
              </>
            )}
            {newButton}
          </div>
        }
      />

      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      {trackingEnabled && compareIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3 text-sm">
          <span className="text-gray-300">
            {compareIds.length} of {EDM_MAX_COMPARE} selected to compare
          </span>
          <button
            type="button"
            className={EDM_UI.primaryButton}
            disabled={compareBusy || compareIds.length < 2}
            onClick={runCompare}
          >
            {compareBusy ? <Spinner size="sm" /> : <BarChart3 className="h-4 w-4" />} Compare
          </button>
          <button
            type="button"
            className={EDM_UI.secondaryButton}
            onClick={() => {
              setCompareIds([]);
              setCompareResult(null);
              setCompareError(null);
            }}
          >
            Clear
          </button>
          {compareIds.length < 2 && <span className="text-xs text-gray-400">Select at least 2 sent campaigns.</span>}
        </div>
      )}

      {trackingEnabled && compareError && <div className={EDM_UI.errorBanner}>{compareError}</div>}

      {trackingEnabled && compareResult !== null && (
        <ComparePanel
          rows={compareResult.rows}
          missing={compareResult.ids.filter((id) => !compareResult.rows.some((r) => r.campaignId === id))}
          nameOf={(id) => compareNames[id] ?? id}
          onClose={() => setCompareResult(null)}
        />
      )}

      {campaigns !== null && campaigns.length === 0 && !error && (
        <div className={EDM_UI.card}>
          <p className="text-sm text-gray-300">
            No campaigns yet. Create one to email your leads, clients or imported contacts.
          </p>
          {newButton}
        </div>
      )}

      {campaigns !== null && campaigns.length > 0 && (
        <div className="rounded-xl border border-gray-700 bg-gray-800/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
                  {trackingEnabled && (
                    <th className="px-4 py-3">
                      <span className="sr-only">Compare</span>
                    </th>
                  )}
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Audience</th>
                  <th className="px-4 py-3">Scheduled</th>
                  <th className="px-4 py-3">Sent / Total</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/leads/edm/${c.id}`)}
                    className="cursor-pointer border-b border-gray-800 hover:bg-gray-800/40"
                  >
                    {trackingEnabled && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Compare ${c.name}`}
                          checked={compareSet.has(c.id)}
                          // Only sent campaigns have numbers to compare; an
                          // eleventh tick is refused (getStats takes 10 at most).
                          disabled={c.sentCount <= 0 || (!compareSet.has(c.id) && compareIds.length >= EDM_MAX_COMPARE)}
                          onChange={() => toggleCompare(c)}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-100">{c.name}</td>
                    <td className="px-4 py-3">
                      <span className={`${EDM_UI.chip} ${statusChipClass(c.status)}`}>{statusLabel(c.status)}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {c.frozenAt && c.counters ? c.counters.recipients.toLocaleString('en-AU') : 'Not frozen'}
                    </td>
                    <td className="px-4 py-3 text-gray-300">{formatBrisbaneDateTime(c.scheduledAt) || '–'}</td>
                    <td className="px-4 py-3 text-gray-300">
                      {c.sentCount.toLocaleString('en-AU')} / {c.totalCount.toLocaleString('en-AU')}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{formatBrisbaneDateTime(c.updatedAt) || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      )}

      {officeScopedAdmin && (
        <p className="text-xs text-gray-400">You see drafts you created and campaigns covering your offices.</p>
      )}
    </div>
  );
};

export default EdmCampaignsPage;
